import React, { useEffect, useState, useContext } from 'react';
import { jwtDecode } from "jwt-decode";
import { googleLogout, useGoogleOneTapLogin } from '@react-oauth/google';
import AuthContext from '.';
import PropTypes from 'prop-types';
import { postPartyGoer, getPartyGoer, updatePartyGoer } from '../apiCalls/partyAPI';
import PartyGoer from '../entities/partyGoer';
import { NotificationContext } from './NotificationContext';

// True if token is not yet expired (used to restore session on refresh)
const isTokenValid = (credential) => {
    try {
        const decoded = jwtDecode(credential);
        const now = Math.floor(Date.now() / 1000);
        return !!(decoded.exp && decoded.exp > now);
    } catch (e) {
        return false;
    }
};

// True if token expires within 5 minutes (used to trigger One Tap proactive refresh)
const isTokenExpiringSoon = (credential) => {
    try {
        const decoded = jwtDecode(credential);
        const now = Math.floor(Date.now() / 1000);
        return !decoded.exp || decoded.exp <= now + 300;
    } catch (e) {
        return true;
    }
};

const AuthContextProvider = ({ children }) => {
    const [ user, setUser ] = useState(null);
    const [ isLoggedIn, setIsLoggedIn ] = useState(false);
    const [ isAdmin, setIsAdmin ] = useState(false);
    const [ authError, setAuthError ] = useState(null);
    const { showToast } = useContext(NotificationContext);



    useGoogleOneTapLogin({
        onSuccess: (credentialResponse) => {
            // Silently refresh — update stored credential without full re-login flow
            const storedUser = JSON.parse(localStorage.getItem('user'));
            if (storedUser) {
                storedUser.credential = credentialResponse.credential;
                localStorage.setItem('user', JSON.stringify(storedUser));
                setUser(storedUser);
                setIsLoggedIn(true);
            } else {
                // No stored user — treat as fresh login
                setUserProfile(credentialResponse);
            }
        },
        onError: () => {
            console.error('Google One Tap failed');
        },
        disabled: isLoggedIn && !isTokenExpiringSoon(user?.credential), // show when token is missing, expired, or expiring soon
        theme: 'light',
        moment_callback: () => {
            // Callback when One Tap UI is ready (can be used for positioning)
        },
        // Position One Tap in bottom-center for better UX
        prompt_parent_id: 'g_id_onload', // Use custom container if available
        context: 'signup', // Show signup-focused UI
    });
    

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        if (storedUser && isTokenValid(storedUser.credential)) {
            setUser(storedUser);
            setIsLoggedIn(true);
        } else {
            // Token is expired or invalid — clear it
            localStorage.removeItem('user');
            setUser(null);
            setIsLoggedIn(false);
            googleLogout();
        }
    }, []);

    const setUserProfile = (user) => {
        const decoded = jwtDecode(user.credential);
        user.email = decoded.email;
        user.firstName = decoded.given_name;
        user.lastName = decoded.family_name;
        setAuthError(null);
        setIsLoggedIn(true); // Set immediately on successful credential
        
        getPartyGoer(user.email, user.credential)
        .then(userFromApi => {
            if (!userFromApi.message) {
                user.partyIds = userFromApi.partyIds;
                user.partyHistory = userFromApi.partyHistory;
                updatePartyGoerInfo(userFromApi, user);
                user.hasAcceptedInvite = userFromApi.hasAcceptedInvite;
                setUser(user);
                localStorage.setItem('user', JSON.stringify(user));
            } else {
                const newPartyGoer = new PartyGoer(user.firstName, user.lastName, user.email, null);
                user.partyIds = null;
                user.activePartyId = null;
                setIsAdmin(false);
                postPartyGoer(newPartyGoer, user.credential)
                .catch(() => {
                    showToast('Unable to reach server. Please try again.', 'error');
                    setAuthError('Unable to reach server. Please try again.');
                });
                setUser(user);
                localStorage.setItem('user', JSON.stringify(user));
            }
        }).catch((error) => {
            console.error('Auth error:', error);
            showToast('Unable to reach server. Please try again.', 'error');
            setAuthError('Unable to reach server. Please try again.');
        });
    };

    const updatePartyGoerInfo = (userFromApi, user) => {
        if (userFromApi.firstName !== user.firstName || userFromApi.lastName !== user.lastName) {
            userFromApi.firstName = user.firstName;
            userFromApi.lastName = user.lastName;
            updatePartyGoer(userFromApi, user.credential)
            .catch(() => {
                showToast('Unable to reach server. Please try again.', 'error');
            });
        }
    };

    const logout = () => {
        setUser(null);
        googleLogout();
        setIsLoggedIn(false);
        setIsAdmin(false);
        setAuthError(null);
        localStorage.removeItem('user');
    };

    const context = {
        isLoggedIn,
        setIsLoggedIn,
        user,
        setUser,
        setUserProfile,
        isAdmin,
        setIsAdmin,
        logout,
        authError,
        setAuthError
    };

    return (
        <AuthContext.Provider value={context}>
            {children}
        </AuthContext.Provider>
    );
};

AuthContextProvider.propTypes = {
    children: PropTypes.object
};

export default AuthContextProvider;
